import { Box, Button, CardMedia, Stack, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { PredictionColumnsLayout } from '../Prediction/PredictionColumnsLayout';
import { useRecordingImage } from '../../api/queries/recordingImage';
import { useMaterialClassification } from '../../api/queries/materialClassification';
import { useEdgeEstimation } from '../../api/queries/edgeEstimation';
import { useSoilClassification } from '../../api/queries/soilClassification';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../Prediction/utils';
import { DetailImageQueryGuarded } from '../DetailView/DetailImageQuery';
import { MaterialColumnQueryGuarded } from '../Prediction/MaterialColumn/MaterialColumn';
import { AttributesColumnQueryGuarded } from '../Prediction/AttributesColumn/AttributesColumn';
import { TrainAuxImagesCardQueryGuarded } from './TrainAuxImagesCard';
import { useTrainAuxCaptures } from '../../api/queries/trainAuxCaptures';
import { TrainRecording } from '../../api/queries/trainRecordingList';
import { BoundingBox } from '../Documentation/ImageCard';
import { fetchWagonNumberImageDataURL, useWagonNumberImage } from '../../api/queries/wagonNumberImage';
import { useSnackbar } from '../SnackbarProvider';

interface TrainRecordingsDetailsProps {
  recording: TrainRecording;
}

export const TrainRecordingsDetails = ({ recording }: TrainRecordingsDetailsProps): ReactElement => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const image = useRecordingImage({ jobsiteId: recording.jobsiteId, recordingId: recording.id });
  const materialClassification = useMaterialClassification({
    jobsiteId: recording.jobsiteId,
    imageId: recording.imageId,
  });
  const displaySoilClassification = useMemo(
    () => shouldDisplaySoilClassification(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const soilClassification = useSoilClassification({
    jobsiteId: recording.jobsiteId,
    imageId: recording.imageId,
    enabled: displaySoilClassification,
  });
  const displayEdgeEstimation = useMemo(
    () => shouldDisplayEdgeEstimation(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const edgeEstimation = useEdgeEstimation({
    jobsiteId: recording.jobsiteId,
    imageId: recording.imageId,
    enabled: displayEdgeEstimation,
  });
  const auxCapturesData = useTrainAuxCaptures({ jobsiteId: recording.jobsiteId, recordingId: recording.id });
  const wagonNumberImage = useWagonNumberImage({
    jobsiteId: recording.jobsiteId,
    recordingId: recording.id,
    enabled: !!recording.wagonNumber,
  });

  const [selectedAttribute, setSelectedAttribute] = useState<string | null>();
  const [edgeSizesBoundingBoxes, setEdgeSizeBoundingBoxes] = useState<BoundingBox[]>([]);
  const [isDownloadingWagonNumberImage, setIsDownloadingWagonNumberImage] = useState(false);

  const handleAttributeSelect = (attributeId: string) => {
    setSelectedAttribute(selectedAttribute === attributeId ? null : attributeId);
  };

  useEffect(() => {
    if (!edgeEstimation.data || edgeEstimation.data.length === 0) return;

    const edgeData = edgeEstimation.data[0];
    if (selectedAttribute === 'edge-estimation') {
      setEdgeSizeBoundingBoxes(edgeData.boundingBoxes);
    } else if (selectedAttribute === undefined && edgeData.status === 'CRITICAL') {
      setSelectedAttribute('edge-estimation');
    } else {
      setEdgeSizeBoundingBoxes([]);
    }
  }, [selectedAttribute, edgeEstimation.data]);

  const downloadFileName = `${dayjs(recording.motionStartAt).format('YYYY-MM-DD_HH-mm-ss')}${
    recording.wagonNumber ? `_${recording.wagonNumber}` : ''
  }`;

  const handleWagonNumberImageDownload = async (): Promise<void> => {
    setIsDownloadingWagonNumberImage(true);
    try {
      const dataUrl = await fetchWagonNumberImageDataURL({
        jobsiteId: recording.jobsiteId,
        recordingId: recording.id,
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${downloadFileName}_wagon-number.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading wagon number image:', error);
      showSnackbar('download.image.error', 'error');
    } finally {
      setIsDownloadingWagonNumberImage(false);
    }
  };

  return (
    <Box height='calc(100% - 5rem - 1px)' display='flex' flexDirection='column' gap={1} padding={2} overflow='auto'>
      {/* Image Card */}
      <DetailImageQueryGuarded
        title={'recording.loadingArea'}
        imageQuery={image}
        downloadFileName={`${downloadFileName}.jpg`}
        boundingBoxes={edgeSizesBoundingBoxes}
      />

      {/* Auxiliary Images */}
      <WrapperCardWithTitle title={'recording.auxiliary.title'} sx={{ flex: 'none', height: '400px' }}>
        <TrainAuxImagesCardQueryGuarded
          auxCapturesQueryData={auxCapturesData}
          jobsiteId={recording.jobsiteId}
          recordingId={recording.id}
        />
      </WrapperCardWithTitle>

      {/* Data Card */}
      <WrapperCardWithTitle
        title={'recording.data'}
        sx={{ flex: 'none', height: 'max-content' }}
        contentSx={{ overflow: 'visible', paddingBottom: 2 }}
      >
        <Box
          boxSizing='border-box'
          sx={{
            width: 'fit-content',
            flex: 'none',
            marginLeft: 2,
            border: '1px solid rgba(0, 0, 0, 0.13)',
            borderRadius: '10px',
            backgroundColor: 'background.paper',
            padding: 2,
          }}
        >
          <Stack gap={2}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              <Stack direction='column'>
                <Typography variant='body2' color='text.secondary'>
                  {t('recording.wagonNumber')}
                </Typography>
                <Typography variant='body1'>{recording.wagonNumber ?? '–'}</Typography>
              </Stack>
              {recording.wagonNumber && wagonNumberImage.data && (
                <CardMedia
                  component='img'
                  src={wagonNumberImage.data}
                  alt={recording.wagonNumber}
                  sx={{
                    width: 56,
                    height: 56,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flex: 'none',
                  }}
                />
              )}
            </Stack>
            {wagonNumberImage.data && (
              <Button
                variant='contained'
                sx={{ width: '100%' }}
                disabled={isDownloadingWagonNumberImage}
                onClick={handleWagonNumberImageDownload}
                startIcon={<FileDownloadIcon />}
              >
                {t('common.download')}
              </Button>
            )}
          </Stack>
        </Box>
      </WrapperCardWithTitle>

      {/* Prediction */}
      <WrapperCardWithTitle title={'recording.prediction'} sx={{ flex: 'none', height: '300px' }}>
        <PredictionColumnsLayout
          materialTypeColumn={<MaterialColumnQueryGuarded materialClassificationQuery={materialClassification} />}
          attributesColumn={
            <AttributesColumnQueryGuarded
              attributeQueries={[soilClassification, edgeEstimation]}
              isMaterialClassificationPending={materialClassification.isPending}
              onAttributeSelect={handleAttributeSelect}
              selectedAttribute={selectedAttribute}
            />
          }
        />
      </WrapperCardWithTitle>
    </Box>
  );
};
